import { NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  Optional,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { GpdrData } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { LabelRequiredPipe } from '../../pipes';
import { ErrorComponent } from '../error';
import { randomId } from '@sc/utils';

@Component({
  selector: 'sc-gpdr',
  styleUrls: ['./gpdr.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgClass,
    MatCheckboxModule,
    ReactiveFormsModule,
    ErrorComponent,
    LabelRequiredPipe,
    TranslateModule,
  ],
  template: `
    <div
      class="main-container"
      [ngClass]="{
        simpleDisplay: simpleDisplay
      }"
    >
      <div
        class="sc-form-title-desktop gpdr-title"
        *ngIf="!simpleDisplay"
        [id]="titleId"
      >
        {{
          isCustomer
            ? ('Gpdr.customer.title' | translate)
            : ('Gpdr.insurer.title' | translate)
        }}
      </div>
      <div
        class="sc-design-text-4 gpdr-content"
        *ngIf="!simpleDisplay"
        [id]="contentId"
      >
        <p>
          {{
            isCustomer
              ? ('Gpdr.customer.content' | translate)
              : ('Gpdr.insurer.content' | translate)
          }}
        </p>
      </div>
      <mat-checkbox
        aria-required="true"
        [aria-describedby]="getAriaDescribedby()"
        labelPosition="after"
        class="sc-design-text-3 gpdr-accept-button"
        [ngClass]="hasError() ? 'error' : ''"
        (change)="onCheckboxClick($event)"
        [checked]="value?.accepted"
        [disabled]="disable"
        [attr.aria-invalid]="hasError() ? true : undefined"
        (focusin)="onFocusin()"
        (focusout)="onFocusout()"
        [attr.data-testid]="'gpdrCheckbox'"
      >
        <span
          *ngIf="!simpleDisplay"
          [innerHTML]="
            isCustomer
              ? ('Gpdr.customer.accept' | translate)
              : ('Gpdr.insurer.accept' | translate)
          "
        ></span>
        <span
          *ngIf="simpleDisplay"
          class="sc-design-text-4-bold"
          [innerHTML]="
            (isCustomer ? 'Gpdr.customer.accept' : 'Gpdr.insurer.accept')
              | labelRequired: true
          "
        >
        </span>
      </mat-checkbox>
    </div>
    <sc-error
      *ngIf="hasError()"
      [id]="errorId"
      [class.sc-conf-error-on-input-focus]="focus"
      [error]="getError()"
      [displayCondition]="hasError()"
    ></sc-error>
  `,
})
export class GpdrComponent implements ControlValueAccessor, OnInit {
  focus: boolean;
  simpleDisplay: boolean;

  titleId: string;
  contentId: string;
  errorId: string;

  @Input() value: GpdrData = null;
  @Input() disable = false;
  @Input() isCustomer = true;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    private readonly translator: TranslatorService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.simpleDisplay = !(this.isCustomer
      ? this.translator.getInstantTranslation('Gpdr.customer.content')
      : this.translator.getInstantTranslation('Gpdr.insurer.content'));
  }

  ngOnInit(): void {
    this.errorId = randomId(8);
    this.titleId = randomId(8);
    this.contentId = randomId(8);
  }

  hasError() {
    return (
      this.ngControl?.invalid &&
      (this.ngControl?.dirty || this.ngControl?.touched)
    );
  }

  getError() {
    return {
      title: this.translator.getInstantTranslation('Gpdr.errorTitle'),
      message: this.translator.getInstantTranslation('Gpdr.errorText'),
    };
  }

  getAriaDescribedby() {
    const describerIdList = [];

    if (this.hasError()) {
      describerIdList.push(this.errorId);
    }

    if (!this.simpleDisplay) {
      describerIdList.push(this.titleId);
      describerIdList.push(this.contentId);
    }

    const resultStr = describerIdList.join(' ');

    return resultStr.trim() || undefined;
  }

  onCheckboxClick($event): void {
    this.value = $event.checked
      ? {
          accepted: $event.checked,
          time: new Date(),
        }
      : null;

    this.onChange(this.value);
  }

  writeValue(value: GpdrData): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disable = isDisabled;
  }

  onFocusin() {
    this.focus = true;
  }

  onFocusout() {
    this.focus = false;
  }
}
